// src/ScanTest.js
//
// Standalone clean-room page for testing the AI Card Identification feature.
// STAGING ONLY — gated by hostname check at the bottom of App.js routing.
//
// Purpose: isolate the AI scan pipeline (upload → edge function → Claude → JSON)
// from the rest of the app. No Checklist.js integration, no card-slot context
// threading. Just: upload front + back, click scan, see what Claude returns.
//
// Scope for first pass: 2024 Chrome Galaxy only. The edge function's prompt
// will be updated separately to understand that set's cards and parallels.

import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';


// Resize + re-encode an image file to keep memory footprint sane before upload.
// Target: max 2200px on longest side, JPEG quality 0.95.
async function compressImage(file) {
  const maxDim = 2200;
  const quality = 0.95;
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, w, h);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error('Compression failed'));
        const compressed = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' });
        resolve(compressed);
      },
      'image/jpeg',
      quality
    );
  });
}

const COLORS = {
  bg: '#04060f',
  panel: '#0a1020',
  border: '#1a2540',
  accent: '#00d4ff',
  gold: '#f0c040',
  text: '#c8d8f0',
  muted: '#4a5a7a',
  green: '#00e87a',
  red: '#ff4444',
};

export default function ScanTest() {
  const [session, setSession] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);

  const [status, setStatus] = useState('idle'); // idle | uploading | scanning | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(null);

  // --- Auth check ---
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session || null);
      setCheckingAuth(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_evt, sess) => {
      setSession(sess || null);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  // --- File handlers ---
  const pickFront = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFrontFile(f);
    setFrontPreview(URL.createObjectURL(f));
    setResult(null);
    setError(null);
  };
  const pickBack = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBackFile(f);
    setBackPreview(URL.createObjectURL(f));
    setResult(null);
    setError(null);
  };

  // --- Scan flow ---
  const runScan = async () => {
    if (!frontFile || !backFile || !session) return;

    setStatus('uploading');
    setError(null);
    setResult(null);
    setElapsedMs(null);
    const start = Date.now();

    try {
      const userId = session.user.id;
      const timestamp = Date.now();
      const frontPath = `${userId}/scan-test-${timestamp}-front.jpg`;
      const backPath = `${userId}/scan-test-${timestamp}-back.jpg`;

      // Compress before upload to keep memory use in the edge function under 256MB
      const [frontC, backC] = await Promise.all([
        compressImage(frontFile),
        compressImage(backFile),
      ]);
      console.log('[ScanTest] compressed sizes:', frontC.size, backC.size);

      // Upload both files to scan-test-images
      const [frontUp, backUp] = await Promise.all([
        supabase.storage.from('scan-test-images').upload(frontPath, frontC, { upsert: true, contentType: 'image/jpeg' }),
        supabase.storage.from('scan-test-images').upload(backPath, backC, { upsert: true, contentType: 'image/jpeg' }),
      ]);
      if (frontUp.error) throw new Error(`Front upload failed: ${frontUp.error.message}`);
      if (backUp.error) throw new Error(`Back upload failed: ${backUp.error.message}`);

      // Get public URLs for the edge function
      const { data: frontPub } = supabase.storage.from('scan-test-images').getPublicUrl(frontPath);
      const { data: backPub } = supabase.storage.from('scan-test-images').getPublicUrl(backPath);

      setStatus('scanning');

      // Call the edge function. The existing function expects a cardContext;
      // for the clean-room test we pass a minimal context that just says
      // "this is from 2024 Chrome Galaxy" and leaves card identity unknown.
      const { data, error: fnError } = await supabase.functions.invoke('extract-card-info', {
        body: {
          frontImageUrl: frontPub.publicUrl,
          backImageUrl: backPub.publicUrl,
          cardContext: {
            setId: '2024-chrome-galaxy',
            subSetId: 'base',
            cardNumber: '96',
            cardName: 'Trouble on Tatooine',
          },
        },
      });

      if (fnError) throw new Error(`Edge function error: ${fnError.message}`);

      setResult(data);
      setElapsedMs(Date.now() - start);
      setStatus('done');
    } catch (err) {
      console.error('[ScanTest] error:', err);
      setError(err.message || String(err));
      setStatus('error');
      setElapsedMs(Date.now() - start);
    }
  };

  const reset = () => {
    setFrontFile(null);
    setBackFile(null);
    setFrontPreview(null);
    setBackPreview(null);
    setStatus('idle');
    setResult(null);
    setError(null);
    setElapsedMs(null);
  };

  // --- Render ---
  if (checkingAuth) {
    return <div style={styles.page}><div style={styles.loading}>Loading…</div></div>;
  }

  if (!session) {
    return (
      <div style={styles.page}>
        <div style={styles.panel}>
          <h1 style={styles.title}>AI CARD SCAN — TEST</h1>
          <p style={styles.paragraph}>
            You must be logged in to use this testbed. Open the main app first, sign in,
            then navigate back to /scan-test.
          </p>
          <a href="/" style={styles.link}>← Back to app</a>
        </div>
      </div>
    );
  }

  const canScan = frontFile && backFile && (status === 'idle' || status === 'done' || status === 'error');

  return (
    <div style={styles.page}>
      <div style={styles.panel}>
        <div style={styles.headerRow}>
          <h1 style={styles.title}>AI CARD SCAN — TEST</h1>
          <span style={styles.badge}>STAGING · 2024 GALAXY · #96</span>
        </div>

        <p style={styles.paragraph}>
          Upload a front and back photo. Click scan. See what Claude returns.
          This is an isolated test page — nothing is added to your collection.
        </p>

        <div style={styles.uploadGrid}>
          <UploadSlot label="FRONT PHOTO" file={frontFile} preview={frontPreview} onPick={pickFront} />
          <UploadSlot label="BACK PHOTO" file={backFile} preview={backPreview} onPick={pickBack} />
        </div>

        <div style={styles.actionRow}>
          <button
            onClick={runScan}
            disabled={!canScan}
            style={{
              ...styles.primaryBtn,
              opacity: canScan ? 1 : 0.4,
              cursor: canScan ? 'pointer' : 'not-allowed',
            }}
          >
            {status === 'uploading' ? 'UPLOADING…' : status === 'scanning' ? 'SCANNING…' : 'SCAN CARD'}
          </button>
          <button onClick={reset} style={styles.secondaryBtn}>RESET</button>
        </div>

        {status === 'error' && (
          <div style={styles.errorBox}>
            <div style={styles.errorTitle}>ERROR</div>
            <pre style={styles.errorBody}>{error}</pre>
            {elapsedMs !== null && <div style={styles.metaLine}>Elapsed: {elapsedMs}ms</div>}
          </div>
        )}

        {status === 'done' && result && (
          <div style={styles.resultWrap}>
            <div style={styles.sectionLabel}>CLAUDE RESPONSE</div>
            {elapsedMs !== null && <div style={styles.metaLine}>Elapsed: {elapsedMs}ms</div>}
            <PrettyResult data={result} />
            <details style={styles.details}>
              <summary style={styles.summary}>Raw JSON</summary>
              <pre style={styles.json}>{JSON.stringify(result, null, 2)}</pre>
            </details>
          </div>
        )}

        <div style={styles.footer}>
          <a href="/" style={styles.link}>← Back to app</a>
        </div>
      </div>
    </div>
  );
}

function UploadSlot({ label, file, preview, onPick }) {
  return (
    <label style={styles.uploadSlot}>
      <div style={styles.uploadLabel}>{label}</div>
      <div style={styles.uploadPreview}>
        {preview ? (
          <img src={preview} alt={label} style={styles.previewImg} />
        ) : (
          <div style={styles.previewEmpty}>+ TAP TO UPLOAD</div>
        )}
      </div>
      <div style={styles.uploadMeta}>
        {file ? `${file.name} · ${(file.size / 1024).toFixed(0)}KB` : 'No file chosen'}
      </div>
      <input type="file" accept="image/*" onChange={onPick} style={{ display: 'none' }} />
    </label>
  );
}

function PrettyResult({ data }) {
  // The existing edge function returns an envelope with an `extracted` object.
  // For the clean-room test we just show every top-level key.
  if (!data || typeof data !== 'object') {
    return <pre style={styles.json}>{String(data)}</pre>;
  }

  // The function returns { success, data: { ... } }
  // Pull the nested data, falling back to the raw root if the envelope is missing.
  const d = (data && data.data) || data || {};
  const conf = d.confidence || {};

  const rows = [
    ['Card name', d.card_name],
    ['Card number', d.card_number],
    ['Serial #', d.serial_number],
    ['Print run', d.serial_print_run],
    ['Parallel', d.parallel_name],
    ['Parallel — evidence', d.parallel_front_evidence],
    ['Serial observation', d.serial_raw_observation],
    ['Graded?', d.is_graded],
    ['Grade company', d.grading_company],
    ['Grade', d.grading_grade],
    ['Cert #', d.grading_cert_num],
    ['Identity match?', d.identity_match],
    ['Identity mismatch reason', d.identity_mismatch_reason],
    ['Notes', d.notes],
    ['Confidence — identity', conf.card_identity],
    ['Confidence — serial', conf.serial_number],
    ['Confidence — grading', conf.grading],
  ];

  return (
    <div style={styles.kvTable}>
      {rows.map(([k, v]) => (
        <div key={k} style={styles.kvRow}>
          <div style={styles.kvKey}>{k}</div>
          <div style={styles.kvValue}>
            {v === null || v === undefined ? <span style={styles.kvNull}>—</span> : String(v)}
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: COLORS.bg,
    color: COLORS.text,
    fontFamily: "'Rajdhani', system-ui, -apple-system, sans-serif",
    padding: '32px 16px',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: COLORS.muted,
    fontFamily: "'Share Tech Mono', monospace",
  },
  panel: {
    maxWidth: '720px',
    margin: '0 auto',
    background: COLORS.panel,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '4px',
    padding: '24px',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '12px',
  },
  title: {
    fontFamily: "'Orbitron', sans-serif",
    fontSize: '20px',
    fontWeight: 800,
    color: COLORS.gold,
    letterSpacing: '3px',
    margin: 0,
  },
  badge: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '10px',
    letterSpacing: '2px',
    padding: '4px 10px',
    color: COLORS.accent,
    border: `1px solid ${COLORS.accent}40`,
    borderRadius: '2px',
    background: `${COLORS.accent}10`,
  },
  paragraph: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: COLORS.muted,
    marginBottom: '20px',
  },
  uploadGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '20px',
  },
  uploadSlot: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: '4px',
    padding: '12px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  uploadLabel: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    letterSpacing: '2px',
    color: COLORS.muted,
  },
  uploadPreview: {
    aspectRatio: '2.5 / 3.5',
    background: COLORS.bg,
    border: `1px dashed ${COLORS.border}`,
    borderRadius: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  previewEmpty: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    letterSpacing: '2px',
    color: COLORS.muted,
  },
  previewImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  uploadMeta: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '10px',
    color: COLORS.muted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  actionRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
  },
  primaryBtn: {
    flex: 1,
    padding: '12px 16px',
    background: 'transparent',
    color: COLORS.accent,
    border: `1px solid ${COLORS.accent}`,
    borderRadius: '2px',
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '12px',
    letterSpacing: '3px',
  },
  secondaryBtn: {
    padding: '12px 16px',
    background: 'transparent',
    color: COLORS.muted,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '2px',
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '12px',
    letterSpacing: '3px',
    cursor: 'pointer',
  },
  errorBox: {
    border: `1px solid ${COLORS.red}`,
    background: `${COLORS.red}10`,
    padding: '12px',
    borderRadius: '2px',
    marginBottom: '16px',
  },
  errorTitle: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    letterSpacing: '2px',
    color: COLORS.red,
    marginBottom: '6px',
  },
  errorBody: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '12px',
    color: COLORS.text,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  },
  resultWrap: {
    marginBottom: '16px',
  },
  sectionLabel: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    letterSpacing: '2px',
    color: COLORS.green,
    marginBottom: '6px',
  },
  metaLine: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '10px',
    color: COLORS.muted,
    marginBottom: '10px',
  },
  kvTable: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: '2px',
    overflow: 'hidden',
    marginBottom: '12px',
  },
  kvRow: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    borderBottom: `1px solid ${COLORS.border}`,
  },
  kvKey: {
    padding: '8px 10px',
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    letterSpacing: '1.5px',
    color: COLORS.muted,
    background: COLORS.bg,
  },
  kvValue: {
    padding: '8px 10px',
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '12px',
    color: COLORS.text,
    wordBreak: 'break-word',
  },
  kvNull: {
    color: COLORS.muted,
  },
  details: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: '2px',
    padding: '8px 10px',
  },
  summary: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    letterSpacing: '2px',
    color: COLORS.muted,
    cursor: 'pointer',
  },
  json: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: COLORS.text,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: '8px 0 0 0',
  },
  footer: {
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: `1px solid ${COLORS.border}`,
    textAlign: 'center',
  },
  link: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    letterSpacing: '2px',
    color: COLORS.accent,
    textDecoration: 'none',
  },
};
